import { breakpoint } from '@zocdoc/components/src/grid';
import { flowRight, noop } from 'lodash';
import clickOutside from 'react-click-outside';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { grey, grey10, grey25, grey50, navy, white } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import { Arrow } from '@zocdoc/components/src/icons';
import { FormattedMessage, injectIntl } from 'react-intl';
import { LOCALIZED_STRINGS, DefaultTexts, SortingEnum } from '../constants';

export class BaseSortingDropdown extends React.Component {
    static propTypes = {
        onApplySort: PropTypes.func,
        onClickDropdown: PropTypes.func,
        onHideDropdown: PropTypes.func,
        onShowDropdown: PropTypes.func,
        sortTypeSelected: PropTypes.string,
        theme: PropTypes.object,
        updateParameters: PropTypes.func,
    };

    static defaultProps = {
        onApplySort: noop,
        onClickDropdown: noop,
        sortTypeSelected: DefaultTexts.sortType,
    };

    state = {
        showDropdown: false,
        selectedValue: this.props.sortTypeSelected,
    };

    componentWillUnmount() {
        if (this.state.showDropdown) {
            this.props.onHideDropdown(this);
        }
    }

    render() {
        return (
            <SortingDropdownWrapper>
                <SortingDropdownButton
                    data-test="facet-chip-button"
                    isActive={this.state.isActive}
                    onClick={this.handleClickFacetChip}
                    showDropdown={this.state.showDropdown}
                >
                    <FormattedMessage
                        {...LOCALIZED_STRINGS.SortingDropdownTitle}
                    />
                    <CurrentSelectedValue>
                        {this.state.selectedValue}
                    </CurrentSelectedValue>
                    <StyledArrow
                        color={navy}
                        direction="down"
                    />
                </SortingDropdownButton>
                {this.renderDropdown()}
            </SortingDropdownWrapper>
        );
    }

    renderDropdown() {
        const { theme } = this.props;
        return (
            <DropdownWrapper
                data-test="sort-by-dropdown"
                hidden={!this.state.showDropdown}
            >
                <FiltersSection>
                    {theme.search.config.sortingOptions.map(key => SortingEnum[key] && (
                        this.renderSortingOption(SortingEnum[key])
                    ))}
                </FiltersSection>
            </DropdownWrapper>
        );
    }

    renderSortingOption(sortingEnum) {
        return (
            <Sorting
                key={sortingEnum.name}
                data-option-selected-text={sortingEnum.text}
                data-option-selected-value={sortingEnum.value}
                data-test={`faceted-search-sorting-${sortingEnum.name}`}
                onClick={this.applySortingOption}
            >
                {sortingEnum.text}
            </Sorting>
        );
    }

    applySortingOption = e => {
        const enumValue = e.target.getAttribute('data-option-selected-value');
        const enumText = e.target.getAttribute('data-option-selected-text');
        if (enumText !== this.state.selectedValue) {
            this.props.onApplySort(enumValue);
            this.props.updateParameters({
                sortType: enumValue,
            });

            this.setState({
                selectedValue: enumText,
            });
        }

        this.exitDropdown();
    }

    handleClickFacetChip = () => {
        this.props.onClickDropdown();
        this.setState(({ showDropdown, scrollToTop }) => ({
            showDropdown: !showDropdown,
            scrollToTop: !scrollToTop,
        }));
        if (!this.state.showDropdown) {
            this.props.onShowDropdown(this);
        } else {
            this.props.onHideDropdown(this);
        }
    }

    handleClickOutside() {
        if (this.state.showDropdown) {
            this.exitDropdown();
        }
    }

    exitDropdown = () => {
        this.setState({
            showDropdown: false,
        });
        this.props.onHideDropdown(this);
    }
}

const SortingDropdownWrapper = styled.div`
    display: none;
    margin-right: 50px;

    ${breakpoint.small} {
        display: inline-block !important;
        position: relative;
    }
`;

const SortingDropdownButton = styled.label`
    background-color: ${white};
    color: ${navy};
    cursor: pointer;
    display: flex;
    font: 13px ${FontFamily.SharpSansMedium};
    margin-right: 12px;
    margin-top: 7px;
    padding: 10px 12px 9px;
    white-space: nowrap;

    &:hover {
        background-color: ${p => p.showDropdown ? grey : grey10};
        color: ${p => p.showDropdown ? white : navy};
        text-decoration: none;
    }
`;

const StyledArrow = styled(Arrow)`
    font-size: 8px;
    margin-top: 5px;
    margin-left: 14px;
`;

const CurrentSelectedValue = styled.div`
    margin-left: 5px;
`;

const DropdownWrapper = styled.div`
    background-color: ${white};
    border-radius: 2px;
    border: solid 1px ${grey50};
    box-shadow: 0 1px 0 0 ${grey};
    left: 0;
    right: 0;
    max-height: 300px;
    overflow-y: scroll;
    position: absolute;
    top: 100%;
    margin-top: 8px;
    z-index: 110;

    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

const FiltersSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const Sorting = styled.div`
    background: ${white};
    color: ${navy};
    display: block;
    font: 13px ${FontFamily.SharpSansMedium};
    padding: 11px 35px 11px;
    padding-left: 18px;
    &:not(:last-child) {
        border-bottom: 1px solid;
        border-color: ${grey25};
    }
    &:hover {
        background-color: ${grey10};
        cursor: pointer;
    }
`;

export default flowRight(
    injectIntl,
    clickOutside,
)(BaseSortingDropdown);
